// The two #4401 local-store helpers: reading the view design inputs, and swapping in a fresh handle.
//
// Extracted from DatabaseWrapper so it can be unit-tested without pulling in
// pouchdb-browser, which references `self` and fails to load under node. It was
// split out of database-wrapper-passthrough.ts, which had become the shared home
// for every testable sync helper regardless of subject (deferred finding
// df-08d4a072f17b).

import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { localOpName } from './local-op-tracker';
import { isLocalStoreNotAnsweringError } from './local-store-timeout';

// ---------------------------------------------------------------------------
// #4401 — reading the two documents the view design doc is built from, without
// the boot being able to wait for ever.
//
// The failure this exists for: the device's own store accepts a read and then
// answers nothing at all — no value, no error. `writeViewDesignDocAsync` awaits
// two such reads, and in production both simply never came back, so the boot
// stopped there in complete silence. Everything downstream waits on
// `whenQueriesReady`, which only `commitQueriesAsync` resolves, and
// `commitQueriesAsync` is what is stuck.
//
// The way out was already in the code and only needed the read to END: the
// existing catch hands the error to `interceptDatabaseError`, and when that
// takes it over the caller returns early — at which point `commitQueriesAsync`
// resolves `whenQueriesReady` and the rest of the app stops hanging. So all
// this helper adds is a time limit that turns "never answers" into a rejection.
//
// It lives here rather than on `DatabaseWrapper` for the usual reason: this
// module is loadable under plain node, so the behaviour can be tested by
// driving it with a read that never settles. `database-wrapper.ts` cannot be
// loaded there at all.
// ---------------------------------------------------------------------------

/** What the design-doc write needs to know before it can build and store the doc (#4401). */
export type ViewDesignInputs = {
  /** Carry on and write the design doc. */
  kind: 'proceed';
  /** Per-view heal salts read from `_local/view-heal`; empty when it could not be read. */
  salts: Record<string, number>;
  /** The existing `_design/view` revision, or undefined when there is no doc to replace. */
  designRev: string | undefined;
  /**
   * True when the design-doc read failed for a reason other than "there is no such
   * document". We then write with no revision, which conflicts if the doc does exist —
   * so the write's own report has to be able to say the revision was never read.
   */
  designReadFailed: boolean;
} | {
  /**
   * Stop: the store is not answering and a recovery has taken the failure over. The
   * caller returns WITHOUT writing the design doc — and, importantly, still resolves
   * `whenQueriesReady`, or every view-backed read hangs until the recovery finishes.
   */
  kind: 'bail';
};

/** Everything `readViewDesignInputsAsync` needs, injected so it can be tested under node (#4401). */
export interface ViewDesignInputsDeps {
  /** Which database this is, for reports. */
  databaseId: string;
  /**
   * Read one document from the LOCAL store. `operationName` is the name this read is
   * recorded under while it is outstanding — it is what a stalled-boot report NAMES, so
   * it is built here rather than at the call site and must stay stable.
   */
  getLocal: (docId: string, operationName: string) => Promise<any>;
  /**
   * Race a local read against the boot time limit, rejecting with a recognisable error
   * when the store does not answer. In production this is `withLocalStoreTimeout`.
   */
  boundLocalRead: <T>(operation: Promise<T>, operationName: string) => Promise<T>;
  /**
   * `DatabaseRecoveryService.interceptDatabaseError`. True means a recovery has taken the failure
   * over and this caller must stop rather than carry on against a store it cannot use.
   */
  intercept: (err: unknown) => boolean;
  /** Report a condition somebody may need to look at later. */
  trackSilentProblem: (name: string, context: Record<string, unknown>) => void;
  /** Extra fields describing an error, for a report. */
  errorFields: (err: unknown) => Record<string, unknown>;
}

/** The id of the per-device view heal salts document (#4219). */
export const VIEW_HEAL_DOC_ID = '_local/view-heal';
/** The id of the document holding every registered view's map function. */
export const VIEW_DESIGN_DOC_ID = '_design/view';

/**
 * Read the heal salts and the existing design doc, with both reads bounded, and say
 * whether the caller may carry on (#4401).
 *
 * Neither read is required for correctness on its own: with no salts we write unsalted
 * views, and with no revision we write without one. What IS required is that this
 * function always finishes — before #4401 it could not, and the whole app waited.
 */
export async function readViewDesignInputsAsync(deps: ViewDesignInputsDeps): Promise<ViewDesignInputs> {
  let salts: Record<string, number> = {};
  // the `(salts)` suffix tells this read apart from the one the one-time global view
  // rebuild makes against the same document (#4343) — without it a report cannot say
  // which of the two stopped answering
  const saltsOperation = localOpName(deps.databaseId, `localDb.get:${VIEW_HEAL_DOC_ID}(salts)`);
  try {
    const doc: any = await deps.boundLocalRead(
      deps.getLocal(VIEW_HEAL_DOC_ID, saltsOperation),
      saltsOperation,
    );
    salts = doc?.salts || {};
  } catch (err) {
    // the store stopped answering — this is not "the salts are unreadable", it is "no
    // local read is going to come back", so hand it to the recovery and stop. Carrying
    // on would only queue the next read behind the same wedge.
    if (deps.intercept(err)) return { kind: 'bail' };
    if (!isPouchNotFound(err)) {
      // an unreadable salt doc must not block view creation — fall back to unsalted
      // views, but surface it: it silently undoes a heal and lets a wedged index come back
      deps.trackSilentProblem('ViewHealSaltReadFailed', {
        db: deps.databaseId, ...deps.errorFields(err),
      });
    }
    salts = {};
  }

  let designRev: string | undefined = undefined;
  let designReadFailed = false;
  const designOperation = localOpName(deps.databaseId, `localDb.get:${VIEW_DESIGN_DOC_ID}`);
  try {
    const designOld: any = await deps.boundLocalRead(
      deps.getLocal(VIEW_DESIGN_DOC_ID, designOperation),
      designOperation,
    );
    designRev = designOld?._rev;
  } catch (err) {
    // L5 (#3986): interceptDatabaseError kicks off DB recovery and we bail early here —
    // the caller (commitQueriesAsync) still resolves whenQueriesReady, or every
    // view-backed read would hang until recovery finishes (or forever if recovery
    // replaces this instance). Recovery re-runs commitQueriesAsync idempotently.
    if (deps.intercept(err)) return { kind: 'bail' };
    if (!isPouchNotFound(err)) {
      // the read failed for a reason we do not recognise. This used to be a bare
      // console.error, which is dev-console only: in production nobody could see it had
      // happened. It is not resolved by anything below — we go on to write the design
      // doc without a revision — so a person may need to look at it.
      designReadFailed = true;
      deps.trackSilentProblem('ViewDesignDocReadFailed', {
        db: deps.databaseId, ...deps.errorFields(err),
      });
    }
  }

  return { kind: 'proceed', salts, designRev, designReadFailed };
}

/**
 * Does the `_design/view` document already stored carry exactly the views this
 * device was trying to write (df-81b53714850e)?
 *
 * Yes means another writer — usually a second tab sharing this local store —
 * committed the same views, so a lost write is harmless here. The comparison is
 * on the rendered map STRINGS rather than the view names, because a map string
 * carries the view's heal salt (#4219) and two writers can mean different
 * content under the same names. Anything unexpected answers false, which is the
 * cautious direction: the caller then reports as it always did.
 *
 * See `design-docs/sync-integrity.md`, "Two tabs of one browser are two
 * writers".
 */
export function storedViewDesignMatchesIntended(
  intendedViews: Record<string, { map: string }>,
  storedDoc: unknown,
): boolean {
  const storedViews = (storedDoc as { views?: unknown } | null | undefined)?.views;
  if (storedViews == null || typeof storedViews !== 'object') return false;

  const intendedNames = Object.keys(intendedViews);
  const storedNames = Object.keys(storedViews as Record<string, unknown>);
  if (intendedNames.length !== storedNames.length) return false;

  // for each view we meant to write, the stored one must carry the identical map string
  for (const name of intendedNames) {
    const stored = (storedViews as Record<string, unknown>)[name];
    const storedMap = (stored as { map?: unknown } | null | undefined)?.map;
    if (typeof storedMap !== 'string') return false;
    if (storedMap !== intendedViews[name].map) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// #4401 — swapping in a fresh local store handle, without the swap itself hanging.
//
// Recreating the connection is the natural remedy for a store that has stopped
// answering, and until #4401 it would have made things worse rather than better.
// Two reasons, both verified against the reports:
//
//  - The old handle was closed with an unbounded `await`. On a store that answers
//    nothing, `close()` answers nothing either — and because it never settles it
//    never throws, so the surrounding catch never ran, the recreation gate was left
//    open, and every read waiting on that gate blocked for ever too. One freeze
//    became a bigger freeze.
//  - Success was reported without ever asking the NEW handle whether it works. A
//    store that is still silent would have been announced as recovered, and the next
//    rung — the one that gives up and tells the user — would never be reached.
//
// So the swap closes within a bound, opens regardless, and then PROVES the new
// handle answers before saying it worked.
// ---------------------------------------------------------------------------

/** Everything the local-store swap needs, injected so it can be tested under node (#4401). */
export interface LocalStoreSwapDeps {
  /** Which database this is, for the log lines. */
  databaseId: string;
  /** Close the old handle. May never settle — that is the case this exists for. */
  closeOld: () => Promise<unknown>;
  /** Construct the replacement handle. Synchronous, like `new PouchDB(...)`. */
  openNew: () => void;
  /** Ask the NEW handle a cheap question, e.g. `info()`. May never settle. */
  probeNew: () => Promise<unknown>;
  /** Race an operation against the local-store time limit. In production, `withLocalStoreTimeout`. */
  bound: <T>(operation: Promise<T>, operationName: string) => Promise<T>;
  log: (message: string) => void;
  /**
   * Asked once the old handle is closed and BEFORE the new one is opened. True means
   * stop: open nothing, probe nothing. The wrapper answers "has my shutdown started",
   * because a shutdown can begin while the close is awaited, and opening a new handle
   * then would revive a database that is being closed on purpose (df-df7525bd9ec9).
   */
  shouldAbandon?: () => boolean;
}

/** What the swap did, so the caller can report it and decide what comes next (#4401). */
export interface LocalStoreSwapOutcome {
  /** True only when the new handle actually answered. */
  ok: boolean;
  /** True when the OLD handle never answered the close — evidence the store was wedged. */
  closeGaveUp: boolean;
  /** True when the NEW handle did not answer either — a fresh connection did not help. */
  probeGaveUp: boolean;
  /**
   * True when `shouldAbandon` stopped the swap after the close: no new handle was
   * opened, so there is nothing to have recovered and nothing that failed. `ok` is
   * true in that case, meaning "nothing is wrong here", not "the store answers".
   */
  abandoned: boolean;
}

/**
 * Close the old local store handle, open a new one, and prove the new one answers (#4401).
 *
 * Never throws: every step here runs while something is already going wrong, and a
 * throw would take out the caller's own recovery. The outcome says what happened
 * instead.
 */
export async function swapLocalStoreAsync(deps: LocalStoreSwapDeps): Promise<LocalStoreSwapOutcome> {
  let closeGaveUp = false;
  try {
    await deps.bound(
      Promise.resolve(deps.closeOld()),
      localOpName(deps.databaseId, 'localDb.close'),
    );
  } catch (err) {
    // Both outcomes are expected here and neither should stop the swap. A store that
    // is wedged answers nothing, so we gave up waiting; a store that is broken throws.
    // Either way the handle is being replaced, so there is nothing to do about it
    // beyond noting which happened.
    closeGaveUp = isLocalStoreNotAnsweringError(err);
    deps.log(`Db:${deps.databaseId}: closing the old local store ${closeGaveUp ? 'never answered' : 'failed'} — replacing it anyway`);
  }

  // if the caller no longer wants a new handle (its shutdown started while the close
  // was awaited), stop here — opening one now would bring the database back to life
  if (deps.shouldAbandon?.()) {
    deps.log(`Db:${deps.databaseId}: the database started shutting down during the swap — not opening a new local store`);
    return { ok: true, closeGaveUp, probeGaveUp: false, abandoned: true };
  }

  deps.openNew();

  let probeGaveUp = false;
  try {
    await deps.bound(
      Promise.resolve(deps.probeNew()),
      localOpName(deps.databaseId, 'localDb.info(after recreate)'),
    );
    return { ok: true, closeGaveUp, probeGaveUp, abandoned: false };
  } catch (err) {
    probeGaveUp = isLocalStoreNotAnsweringError(err);
    deps.log(`Db:${deps.databaseId}: the replacement local store ${probeGaveUp ? 'did not answer either' : 'failed its first read'} — recreation did not help`);
    return { ok: false, closeGaveUp, probeGaveUp, abandoned: false };
  }
}
